import { createSelector } from '@reduxjs/toolkit';

import { findActiveOption } from 'src/redux/productsQuery/helpers/facetOptionHelpers';
import {
  type TaxonomyContext,
  selectTaxonomyContext,
} from 'src/redux/productsQuery/selectors/selectTaxonomyContext';

/**
 * The label of the lowest (deepest) active taxonomy level — e.g. "earrings" for
 * `/shop/all/jewellery/earrings`. Walks up from the deepest level to the first
 * single-valued selection, mirroring how the breadcrumb trail is built, so a
 * multi-selected deepest level is skipped rather than rendered ambiguously.
 * Undefined when no taxonomy level is active (the surface root).
 *
 * Returned verbatim: character-casing is the consumer's responsibility (the UI
 * component or tracking code that renders it), not the selector's. Used as the
 * fallback label for both the shop PLP's HTML title and its h1 when the page has
 * no matched content heading.
 */
const getLowestActiveTaxonomyLabel = ({
  facets,
  taxonomyAttributeAliases,
  valuesPerLevel,
}: TaxonomyContext): string | undefined => {
  for (let i = valuesPerLevel.length - 1; i >= 0; i--) {
    if (valuesPerLevel[i]!.length === 1) {
      return findActiveOption(facets, taxonomyAttributeAliases[i]!)?.title;
    }
  }

  return undefined;
};

export const selectLowestActiveTaxonomyLabel = createSelector(
  [selectTaxonomyContext],
  getLowestActiveTaxonomyLabel,
);
